<template>
  <modal v-if="formVisible"
         :close-on-click-outside="false"
         :button2-disabled="() => { return isEmpty(subject) || isEmpty(message); }"
         :button1="{ label: 'Abbrechen', icon: 'close', color: 'red' }"
         :button2="{ label: 'Senden', icon: 'save', color: 'green', onClick: submit }"
         :hide-on-submit="false"
         :hide-buttons="status === 'success'"
         title="Nachricht hinterlassen"
         @destroy="onDestroy">
    <template v-if="status === 'success'">
      <hm-card-inner>
        <hint :text="`Ticket wurde #${ticketId} erfolgreich erstellt.`" icon="ok" />
      </hm-card-inner>
    </template>
    <template v-else-if="['edit', 'error'].includes(status)">
      <hm-card-inner :full-size="true">
        <hm-col cols="12">
          <span class="warning-text warning-text--full-size warning-text--no-margin col-12">Bitte überprüfe ob deine Ihre Frage in unserer <a href="https://docs.hotelmeister.io/" target="_blank">Dokumentation</a> bereits beantwortet wurde, <b>bevor</b> du unseren Support kontaktierst.</span>
        </hm-col>
      </hm-card-inner>
      <hm-card-inner>
        <hm-input v-model="subject" label="Betreff" cols="12" />
        <hm-input-select id="inquiryType"
                         v-model="inquiryType"
                         :validate="'required'"
                         no-select-label
                         cols="12"
                         label="Art der Anfrage">
          <option :value="null" disabled>[Bitte auswählen]</option>
          <option value="general">Allgemeine Anfrage</option>
          <option value="bookability">Frage zur Buchbarkeit</option>
        </hm-input-select>
        <template v-if="['bookability'].includes(inquiryType)">
          <hm-col cols="12">
            <span class="info-text col-12">
              Bitte hilft uns, mit einem konkreten Beispiel, Rückfragen zu reduzieren.
            </span>
          </hm-col>
          <hm-input-date v-model="arrivalDate" cols="12 6@tablet" label="Anreisedatum" />
          <hm-input-date v-model="departureDate" cols="12 6@tablet" label="Abreisedatum" :min-date="arrivalDate"/>
          <hm-input-select v-model="roomType" cols="12 6@tablet" label="Zimmerkategorie">
            <option v-for="roomType in hotelSettings.roomTypes.all()" :value="roomType.name[hotelSettings.details.primaryLanguage]">{{ roomType.name[hotelSettings.details.primaryLanguage] }}</option>
          </hm-input-select>
          <hm-input-select v-model="ratePlan" cols="12 6@tablet" label="Tarif">
            <option v-for="ratePlan in hotelSettings.ratePlans.all()" :value="ratePlan.name[hotelSettings.details.primaryLanguage]">{{ ratePlan.name[hotelSettings.details.primaryLanguage] }}</option>
          </hm-input-select>
          <hm-input-number v-model="numAdults" cols="12 6@tablet" label="Anzahl Erwachsene" />
          <hm-input v-model="numChildren" cols="12 6@tablet" label="Anzahl+Alter Kinder" />
          <hm-input v-model="channel" cols="12" label="Buchungskanal" />
        </template>
        <hm-col cols="12">
          <span class="info-text col-12"><b>Übermittle möglichst viele Informationen zu deiner Fragestellung.</b>
            <span>
              <p style="position: relative; display: block; margin-top: 4px;margin-bottom: 0px;">Anfragen ohne Details wie &quot;???&quot;, &quot;funktioniert nicht&quot;, &quot;Rückruf&quot; oder &quot;dringend&quot;, verzögern die Bearbeitung und erfordern Rückfragen.</p>
            </span>
          </span>
        </hm-col>
        <hm-textarea v-model="message"
                     :input-rows="8"
                     :lazy="false"
                     :disabled="isEmpty(inquiryType)"
                     label="Nachricht"
                     cols="12" />
        <hm-input-select v-model="type" :help="typeHelpText" label="Typ">
          <option value="question">Frage</option>
          <option value="task">Einstellungsänderung</option>
          <option value="incident">Fehlfunktion</option>
          <option value="problem">Störung/Ausfall</option>
        </hm-input-select>
        <template v-if="!['question','task'].includes(type)">
          <span class="support-form--info-text" style="padding-top: 1rem;">
            Durch die richtige Klassifizierung Ihrer Anfrage, helfen Sie uns Ihre Anfrage schnellstmöglich zu bearbeiten.
          </span>
          <hm-input-select v-model="priority" label="Fehlerklasse">
            <option value="urgent">Klasse 1 - Nutzung der Software Hotelmeister ist nicht möglich oder unzumutbar eingeschränkt.</option>
            <option value="high">Klasse 2 - Zweckmäßige Nutzung der Software Hotelmeister ist ernstlich eingeschränkt.</option>
            <option value="normal">Klasse 3 - Zweckmäßige Nutzung der Software Hotelmeister ist leicht eingeschränkt.</option>
            <option value="low">Klasse 4 -Nutzung der Software Hotelmeister ist ohne Einschränkung möglich.</option>
          </hm-input-select>
          <span v-if="priority === 'urgent'" class="support-form--info-text">
            <b>Klasse 1</b> - Die Nutzung der Software Hotelmeister ist nicht möglich oder unzumutbar eingeschränkt. Der Fehler hat schwerwiegenden Einfluss auf wesentliche Funktionen und / oder die Sicherheit der Software; die Leistung kann nicht weiterverwendet werden.<br/>
          </span>
          <span v-if="priority === 'high'" class="support-form--info-text">
            <b>Klasse 2</b> - Die zweckmäßige Nutzung der Software Hotelmeister ist ernstlich eingeschränkt. Der Fehler hat wesentlichen Einfluss auf Funktionen und/oder die Sicherheit der Software Hotelmeister, lässt aber eine Weiterverwendung der erbrachten Leistung zu.<br/>
          </span>
          <span v-if="priority === 'normal'" class="support-form--info-text">
            <b>Klasse 3</b> - Die zweckmäßige Nutzung der Software Hotelmeister ist leicht eingeschränkt. Der Fehler hat unwesentlichen Einfluss auf die Funktionalität und/oder die Sicherheit der Software Hotelmeister und lässt eine weitere Verwendung der Software mit nur geringen Einschränkungen zu.<br/>
          </span>
          <span v-if="priority === 'low'" class="support-form--info-text">
            <b>Klasse 4</b> - Die zweckmäßige Nutzung der Software Hotelmeister ist ohne Einschränkung möglich. Der Fehler hat keinen oder nur unerheblichen Einfluss auf die Funktionalität und/oder die Sicherheit der Software Hotelmeister. Die Nutzung der Software Hotelmeister bleibt uneingeschränkt möglich
          </span>
        </template>
        <hm-col v-if="isVacation" cols="12">
          <span class="warning-text col-12"><b>Betriebsurlaub</b>
            <small style="position: relative; display: block; margin-top: 4px;">Wir befinden uns <b>bis zum {{ vacationEndDate | toDate }} auf Betriebsurlaub</b> und werden deine Support-Anfrage nach unserer Rückkehr bearbeiten. In dringenden Fällen versuchen wir, auch während dieser Zeit zu helfen. Sämtliche Systeme werden selbstverständlich auch während unseres Urlaubs rund um die Uhr überwacht.</small>
          </span>
        </hm-col>
      </hm-card-inner>
    </template>
  </modal>
</template>

<script>

import HotelmeisterMixin from '../../../mixins/HotelmeisterMixin';
import TimerMixin from '../../../mixins/TimerMixin';
import Modal from './Modal';
import HmCardInner from '../layout/HmCardInner';
import HmCol from '../layout/HmCol';
import uuid from 'uuid';
import { isEmpty, isNil } from 'lodash';
import VacationMixin from '../../../mixins/VacationMixin';
import FilterMixin from '../../../mixins/FilterMixin';
import HmInputSelect from '../inputs/HmInputSelect.vue';
import HmInputDate from '../inputs/HmInputDate.vue';
import HmInputNumber from '../inputs/HmInputNumber.vue';
import HmInput from '../inputs/HmInput.vue';

export default {
  name: 'SupportForm',
  components: { HmInput, HmInputNumber, HmInputDate, HmInputSelect, Modal, HmCardInner, HmCol },

  mixins: [HotelmeisterMixin, TimerMixin, VacationMixin, FilterMixin],

  data() {
    return {
      formVisible: false,
      subject: null,
      message: null,
      type: 'question',
      priority: 'low',
      ip: null,
      status: 'edit',
      ticketId: null,
      inquiryType: null,
      arrivalDate: null,
      departureDate: null,
      roomType: null,
      ratePlan: null,
      numAdults: 2,
      numChildren: null,
      channel: null,
    };
  },
  computed: {
    computedMessage() {
      if (['bookability'].includes(this.inquiryType)) {
        return `
Anfragetype: ${this.inquiryType || '-'}
Anreisedatum: ${this.arrivalDate || '-'}
Abreisedatum: ${this.departureDate || '-'}
Zimmerkategorie: ${this.roomType || '-'}
Tarif: ${this.ratePlan || '-'}
Anzahl Erwachsene: ${this.numAdults || '-'}
Anzahl+Alter Kinder: ${this.numChildren || '-'}
Buchungskanal: ${this.channel || '-'}

Nachricht:
${this.message || '-'}
        `.trim();
      } else {
        return this.message;
      }
    },
    typeHelpText() {
      if (isNil(this.type)) return null;

      switch (this.type) {
        case 'question': return 'Sie haben eine Frage zur Bedienung der Software Hotelmeister';
        case 'task': return 'Sie benötigen Unterstützung bei der Änderung von Einstellungen oder Vorlagen';
        case 'incident': return 'Bei einem einzelnen Objekt (einer Reservierung, einem Meldeblatt, einer Rechnung) tritt ein Fehler auf';
        case 'problem': return 'Bei mehreren Funktionen und Objekten (z.B. bei allen Rechnungen, bei allen Meldeblätter) tritt ein Fehler auf';
        default: return null;
      }
    },
  },

  watch: {
    type: function (newVal) {
      switch (newVal) {
        case 'incident':
          this.priority = 'normal';
          break;
        case 'problem':
          this.priority = 'high';
          break;
        case 'question':
        case 'task':
        default:
          this.priority = 'low';
          break;
      }
    },
  },

  created: function () {
    this.$serviceBus.$on('supportForm.show', this.showForm);

    try {
      fetch('https://api64.ipify.org?format=json').then(response => response.json()).then((data) => { this.ip = data.ip; });
    } catch (e) { /* ignore */ }
  },

  beforeDestroy: function () {
    this.$serviceBus.$off('supportForm.show', this.showForm);
  },


  methods: {
    submit() {
      let tenant = 'unknown';
      if (!isNil(this.hotelSettings) && !isNil(this.hotelSettings.hotel) && !isEmpty(this.hotelSettings.hotel.hotelName)) {
        tenant = `${this.hotelSettings.hotel.hotelName} (${this.hotelSettings.hotel.tenant.subdomain})`;
      }
      let user = 'unknown';
      if (!isNil(this.$root.username) && !isNil(this.$root.user)) {
        user = `${this.$root.username}@${this.$root.user.tenantSubdomain}`;
      }
      const req = {
        ticket: {
          comment: `${this.computedMessage}\n\nURL: ${window.location.href}\nIP: ${this.ip}\nClient: ${navigator.userAgent}`,
          priority: this.priority,
          type: this.type,
          subject: this.subject,
          custom_fields: [
            { id: 4814871564306, value: tenant },
            { id: 4814889627666, value: user },
          ],
          metadata: {
            system: {
              ip_address: this.ip,
              client: navigator.userAgent,
            },
          },
          requester: {
            name: user,
            email: this.$root.user.email || this.hotelSettings.details.emailReservation || '-',
          },
        },
      };

      this.axios.post(
        'https://c7m22g0034.execute-api.eu-central-1.amazonaws.com/live/api/v2/tickets.json',
        req,
        {
          headers: {
            'x-api-key': 'Tx1yfrAc9y75COS4MzmEH9nQNOdKWsK96ZL8jUTT',
          },
        },
      )
        .then((response) => {
          this.showSuccessMessage();
          this.status = 'success';
          this.ticketId = response.data.audit.ticket_id;
          this.$rollbar.error(`Ticket #${this.ticketId}`);
          this.setTimeout(() => {
            this.formVisible = false;
            this.status = 'edit';
          }, 5000);
        })
        .catch((error) => {
          this.showErrorMessage('Fehler beim Senden der Anfrage.');
          this.setTimeout(() => {
            this.status = 'error';
          }, 2500);
        });
    },
    onDestroy() {
      this.formVisible = false;
      this.ticketId = null;
      this.status = 'edit';
    },
    showForm() {
      this.ticketId = null;
      this.subject = null;
      this.message = null;
      this.inquiryType = null;
      this.arrivalDate = null;
      this.departureDate = null;
      this.roomType = null;
      this.ratePlan = null;
      this.numAdults = 2;
      this.numChildren = null;
      this.channel = null;
      this.status = 'edit';
      this.priority = 'low';
      this.type = 'question';
      this.formVisible = true;
    },
  },
};
</script>

<style lang="scss">
  @import '~styles/import';

  .support-form {
    &--info-text {
      color: $gray;
      font-size: 100%;
      padding-bottom: 1.6rem;
      grid-column-start: span 12;
    }
    &--documentation-text {
      background-color: $lightmint;
      color: $gray;
      font-size: 100%;
      padding-bottom: 1.6rem;
      grid-column-start: span 12;
      line-height: 2rem;
      a {
        color: $mint;
        text-decoration: underline;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/lib/util/SupportForm.vue